import {GetListResponse, ParamsOptions} from "@/src/modules/core/types";

import {meService} from "../../core/services/me";
import {householdApi} from "../api";
import {getFilenameFromResponseHeaders} from "../lib/bulk-import-download";
import {parseHouseholdDeactivateConfirmationError} from "../lib/household-status-errors";
import type {
  AmenityAccessRestrictionMutationResponse,
  DeactivateResidentRequest,
  DeactivateResidentResult,
  Household,
  HouseholdAmenityRestrictionHistoryRow,
  ResidentTurnoverStatus,
} from "../types";
import type {HouseholdStatusInput, HouseholdStatusResult} from "../types/household-status";

class HouseholdService {
  async getHouseholds(options: ParamsOptions): Promise<GetListResponse<Household>> {
    const {data} = await householdApi.getHouseholds(options);
    const raw = data as {data?: Household[]; meta?: {total: number}};
    const households = Array.isArray(raw.data) ? raw.data : [];
    const meta = raw.meta ?? {total: 0};
    return {data: households, meta};
  }

  async getHousehold(id: number): Promise<Household> {
    const {data} = await householdApi.getById(id);
    return (data as {data: Household}).data;
  }

  async getResidentTurnoverStatus(id: number): Promise<ResidentTurnoverStatus> {
    const {data} = await householdApi.getResidentTurnoverStatus(id);
    return data.data;
  }

  async deactivateResident(
    id: number,
    body: DeactivateResidentRequest,
  ): Promise<DeactivateResidentResult> {
    const {data} = await householdApi.deactivateResident(id, body);
    return data.data;
  }

  async getResidentWelcomePdfBlob(id: number): Promise<{blob: Blob; filename: string | null}> {
    const response = await householdApi.downloadResidentWelcomePdf(id);

    return {
      blob: response.data,
      filename: getFilenameFromResponseHeaders(
        response.headers as Record<string, string | string[] | undefined>,
      ),
    };
  }

  getCanDeactivateTurnover(): boolean {
    return meService.getHasPermission("resident turnover deactivate");
  }

  getCanShowTurnover(): boolean {
    return meService.getHasPermission("resident turnover show");
  }

  /** Mutations in the turnover sheet (deactivate, onboard, welcome PDF). */
  getCanManageTurnover(): boolean {
    return this.getCanDeactivateTurnover();
  }

  /** Open sheet and load turnover status. */
  getCanReadTurnover(): boolean {
    return this.getCanShowTurnover();
  }

  async createHousehold(payload: Record<string, unknown>): Promise<Household> {
    const {data} = await householdApi.create(payload);
    return (data as {data: Household}).data;
  }

  async updateHousehold(id: number, payload: Record<string, unknown>): Promise<Household> {
    const {data} = await householdApi.update(id, payload);
    return (data as {data: Household}).data;
  }

  async deleteHousehold(id: number): Promise<void> {
    await householdApi.delete(id);
  }

  async updateHouseholdStatus(
    id: number,
    body: HouseholdStatusInput,
  ): Promise<{data: HouseholdStatusResult; message?: string}> {
    try {
      const {data} = await householdApi.updateStatus(id, body);
      const envelope = data as {
        data: HouseholdStatusResult;
        message?: string;
      };
      return {data: envelope.data, message: envelope.message};
    } catch (error) {
      const confirmation = parseHouseholdDeactivateConfirmationError(error);
      if (confirmation) throw confirmation;
      throw error;
    }
  }

  getCanUpdateHousehold(): boolean {
    return meService.getHasPermission("household update");
  }

  async postAmenityAccessRestriction(
    id: number,
    body: Record<string, unknown>,
  ): Promise<{data: AmenityAccessRestrictionMutationResponse; message?: string}> {
    const {data} = await householdApi.postAmenityAccessRestriction(id, body);
    const envelope = data as {
      data: AmenityAccessRestrictionMutationResponse;
      message?: string;
    };
    return {data: envelope.data, message: envelope.message};
  }

  async getAmenityAccessRestrictions(
    id: number,
    options: {page?: number; per_page?: number} = {},
  ): Promise<GetListResponse<HouseholdAmenityRestrictionHistoryRow>> {
    const {data} = await householdApi.getAmenityAccessRestrictions(id, options);
    const raw = data as {
      data?: HouseholdAmenityRestrictionHistoryRow[];
      meta?: {
        total: number;
        current_page: number;
        last_page: number;
        per_page: number;
      };
    };
    return {
      data: raw.data ?? [],
      meta: raw.meta ?? {total: 0, current_page: 1, last_page: 1, per_page: 15},
    };
  }
}

export const householdService = new HouseholdService();
